import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Person, Role, PersonWithRole } from '../../types';
import { searchPeople, createPerson, searchRoles, createRole } from '../../api';
import UnifiedAutocomplete from '../autocomplete/UnifiedAutocomplete';

interface PersonRoleSelectorProps {
  selectedPersonRoles: PersonWithRole[];
  onPersonRoleAdd: (personWithRole: PersonWithRole) => void;
  onPersonRoleRemove: (index: number) => void;
  placeholder?: string;
}

const PersonRoleSelector: React.FC<PersonRoleSelectorProps> = ({
  selectedPersonRoles,
  onPersonRoleAdd,
  onPersonRoleRemove,
  placeholder
}) => {
  const { t } = useTranslation();
  const [personName, setPersonName] = useState('');
  const [roleName, setRoleName] = useState('');

  const handleAddPersonRole = async () => {
    if (!personName.trim()) return;

    try {
      // Find or create person
      const people = await searchPeople(personName.trim(), 1);
      let person: Person;
      if (people.length > 0 && people[0].name.toLowerCase() === personName.trim().toLowerCase()) {
        person = people[0];
      } else {
        person = await createPerson({ name: personName.trim() });
      }

      // Find or create role if provided
      let role: Role | null = null;
      if (roleName.trim()) {
        const roles = await searchRoles(roleName.trim(), 1);
        if (roles.length > 0 && roles[0].name.toLowerCase() === roleName.trim().toLowerCase()) {
          role = roles[0];
        } else {
          role = await createRole({ name: roleName.trim() });
        }
      }

      const personWithRole: PersonWithRole = {
        person,
        role
      };

      onPersonRoleAdd(personWithRole);
      setPersonName('');
      setRoleName('');
    } catch (error) {
      
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        {/* Person Input */}
        <div className="flex-1">
          <UnifiedAutocomplete
            mode="string"
            value={personName}
            onChange={setPersonName}
            searchFunction={async (query: string, limit?: number) => {
              const people = await searchPeople(query, limit);
              return people.map(person => person.name);
            }}
            placeholder={t('placeholder.searchOrAddPersonName')}
            debounceMs={600}
            createButtonText={(query) => `+ ${t('common.add')} "${query}"`}
            searchingText={t('common.searching')}
            noItemsFoundText={(query) => `${t('common.noPeopleFound')} "${query}"`}
          />
        </div>

        {/* Role Input */}
        <div className="w-40">
          <UnifiedAutocomplete
            mode="string"
            value={roleName}
            onChange={setRoleName}
            searchFunction={async (query: string, limit?: number) => {
              const roles = await searchRoles(query, limit);
              return roles.map(role => role.name);
            }}
            placeholder={t('placeholder.roleOptional')}
            debounceMs={600}
            allowClear={true}
            createButtonText={(query) => `+ ${t('common.add')} "${query}"`}
            searchingText={t('common.searching')}
            noItemsFoundText={(query) => `${t('common.noRolesFound')} "${query}"`}
          />
        </div>

        {/* Add Button */}
        <button
          type="button"
          onClick={handleAddPersonRole}
          disabled={!personName.trim()}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('common.add')}
        </button>
      </div>

      {selectedPersonRoles.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedPersonRoles.map((personRole, index) => (
            <span
              key={`${personRole.person.id}-${personRole.role?.id || 'no-role'}-${index}`}
              className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm max-w-full ${
                personRole.role ? 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200' : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  personRole.role ? 'bg-white dark:bg-gray-800 opacity-70' : 'bg-gray-500 opacity-50'
                }`}
              />
              <span className="truncate">{personRole.person.name}</span>
              {personRole.role && (
                <>
                  <span className="text-white opacity-70 flex-shrink-0">•</span>
                  <span className="truncate">{personRole.role.name}</span>
                </>
              )}
              <button
                type="button"
                onClick={() => onPersonRoleRemove(index)}
                className={`ml-1 flex-shrink-0 ${
                  personRole.role ? 'text-white hover:text-gray-200' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default PersonRoleSelector;